<template>
  <div id="portfolio-by-broker">
    <v-card
      class="py-2 mt-4 mb-0 elevation-0"
      :class="{
        'px-4': isMobile,
        'px-5': !isMobile,
        'sticky-filters': user.preferences.stickyFilters,
      }"
    >
      <PositionsFiltersAndViewMode
        ref="positionsFilters"
        type="position"
        :positionType.sync="positionType"
        :showZeroPositions="showZeroPositions"
        @filterPositions="filterPositions"
      >
        <template v-slot:buttons>
          <MoreButtons
            :buttons="moreButtons"
            :loading="loadingExport"
            :text="$t('views.portfolio.menu.text')"
            :class="{
              'ml-3': isMobile,
            }"
          />
        </template>
      </PositionsFiltersAndViewMode>
    </v-card>

    <MarketsWarning :positionSummaryDetail="positionSummaryDetail" />
    <SomePositionIncomplete :autoLoad="false" :positionSummaryDetail="positionSummaryDetail" />
    <SomePendingRecreatePosition
      :somePendingRecreatePosition="
        positionSummaryDetail && positionSummaryDetail.somePendingRecreatePosition
      "
    />

    <v-row v-if="brokerAll">
      <v-col cols="12" lg="12" class="mt-0 py-0">
        <v-row>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <TopPortfolioDailyPl
              :summary="positionSummaryDetail"
              :loadingSummary="loadingSummary"
            />
          </v-col>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <TopPortfolioValue :summary="positionSummaryDetail" :loadingSummary="loadingSummary" />
          </v-col>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <TopPortfolioTotalPl
              :summary="positionSummaryDetail"
              :loadingSummary="loadingSummary"
            />
          </v-col>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <TopPortfolioDividends
              :summary="positionSummaryDetail"
              :loadingSummary="loadingSummary"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-card
      id="portfolio-by-broker-card"
      class="px-5 py-4 mb-5 elevation-0"
      :class="{ 'mt-0': brokerAll, 'mt-4': !brokerAll }"
    >
      <PortfolioTable
        ref="portfolioTable"
        :broker="broker"
        :type="positionType"
        :filters="filters"
        :showZeroPositions="showZeroPositions"
        @updateFilters="updateFiltersAndLoadSummaries"
        @editPosition="editPosition"
        @tickerClicked="openPositionDetail"
        @filterByTag="filterByTag"
        @loadAllPortfolioByBroker="loadAllPortfolioByBroker"
      />
    </v-card>

    <PositionModal
      :isModalActive.sync="isPositionModalActive"
      :editItem="editItem"
      :editing="editing"
      @saved="loadAllPortfolioByBroker"
    />

    <PositionDetailDrawer
      :active.sync="positionTableDrawerActive"
      :position="positionDetail"
      @editPosition="editPosition"
    />
  </div>
</template>

<script>
import gtag from '@/lib/gtag'
import { mapState } from 'vuex'
import { get } from 'lodash'
import { getPositionSummaryAndDetail, getPosition, downloadPositionsCsv } from '@/lib/api.js'
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import PositionsFiltersAndViewMode from '@/views/components/common/positions/PositionsFiltersAndViewMode'
import PortfolioTable from '@/views/components/portfolio/PortfolioTable'
import PositionModal from '@/views/components/common/positions/PositionModal'
import PositionDetailDrawer from '@/views/components/portfolio/PositionDetailDrawer'
import TopPortfolioDailyPl from '@/views/components/portfolio/TopPortfolioDailyPl'
import TopPortfolioValue from '@/views/components/portfolio/TopPortfolioValue'
import TopPortfolioTotalPl from '@/views/components/portfolio/TopPortfolioTotalPl'
import TopPortfolioDividends from '@/views/components/portfolio/TopPortfolioDividends'
import MoreButtons from '@/views/components/common/MoreButtons'
import MarketsWarning from '@/views/components/common/positions/MarketsWarning'
import SomePositionIncomplete from '@/views/components/common/positions/SomePositionIncomplete.vue'
import SomePendingRecreatePosition from '../common/positions/SomePendingRecreatePosition.vue'

export default {
  components: {
    PositionsFiltersAndViewMode,
    PortfolioTable,
    PositionModal,
    PositionDetailDrawer,
    TopPortfolioDailyPl,
    TopPortfolioValue,
    TopPortfolioTotalPl,
    TopPortfolioDividends,
    MoreButtons,
    MarketsWarning,
    SomePositionIncomplete,
    SomePendingRecreatePosition,
  },
  props: {
    broker: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    positionType: null,
    filters: {},
    showZeroPositions: false, // TODO
    isPositionModalActive: false,
    editing: false,
    editItem: {},
    positionTableDrawerActive: false,
    positionDetail: null,
    loadingSummary: false,
    positionSummaryDetail: null,
    loadingExport: false,
    loadTimeout: null,
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    brokerAll() {
      return this.broker.name === 'all'
    },
    moreButtons() {
      const moreButtons = []
      if (this.brokerAll) {
        moreButtons.push({
          value: 'showZeroPositions',
          title: this.$t('views.portfolio.menu.showZeroPositions'),
          icon: 'mdi-numeric-0-box-multiple-outline',
          actionable: 'switch',
          action: this.changeShowZeroPositions,
          divider: 'bottom',
        })
        moreButtons.push({
          value: 'add',
          title: this.$t('views.portfolio.menu.addNewPosition'),
          icon: 'mdi-plus',
          action: this.createPosition,
        })
      }
      moreButtons.push({
        value: 'download',
        title: this.$t('views.portfolio.menu.downloadPositionsCsv'),
        icon: 'mdi-cloud-download-outline',
        action: this.downloadPortfolioCsv,
      })
      if (this.brokerAll) {
        moreButtons.push({
          value: 'customHeaders',
          title: this.$t('views.portfolio.menu.customHeaders'),
          icon: 'mdi-cog-outline',
          action: this.customHeaders,
        })
      }
      return moreButtons
    },
  },
  watch: {
    '$route.query.position': {
      async handler() {
        if (this.brokerAll && this.$route.query.position) {
          const position = await getPosition(this.$route.query.position)
          this.openPositionDetail(position.data)
        }
      },
      deep: true,
      immediate: true,
    },
    positionType: {
      async handler() {
        if (this.positionType) {
          // console.log('PortfolioByBroker: watch positionType', this.positionType)

          this.loadSummaries()

          gtag.pageview({
            pagePath: '/portfolio',
            pageTitle: 'portfolio',
            pageContentGroup: 'page',
            extraParams: {
              filter: `type|${this.positionType}`,
            },
          })
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.positionType = get(this.user, 'preferences.defaultPositionsNeCash', null)
      ? 'allNeCash'
      : 'all'

    if (this.$refs.positionsFilters) this.$refs.positionsFilters.updateFilters()
  },
  async destroyed() {
    clearTimeout(this.loadTimeout)
  },
  methods: {
    changeShowZeroPositions() {
      this.showZeroPositions = !this.showZeroPositions
    },
    async updateFiltersAndLoadSummaries() {
      // console.log('PortfolioByBroker: updateFiltersAndLoadSummaries')
      if (this.$refs.positionsFilters) this.$refs.positionsFilters.updateFilters()
      this.loadSummaries()
    },
    async filterPositions(filters) {
      this.filters = JSON.parse(JSON.stringify(filters))
      // console.log('PortfolioByBroker: filterPositions', filters)
      this.loadSummaries()
    },
    async loadSummaries() {
      if (!this.brokerAll) return

      // console.log('PortfolioByBroker: loadSummaries')

      this.loadingSummary = true

      try {
        const filters = JSON.parse(JSON.stringify(this.filters))
        const type =
          this.positionType === 'all'
            ? undefined
            : this.positionType === 'allNeCash'
            ? { $ne: 'cash' }
            : this.positionType
        const positionSummaryDetail = await getPositionSummaryAndDetail({
          filter: {
            type,
            ...filters,
          },
        })
        this.positionSummaryDetail = positionSummaryDetail.data

        clearTimeout(this.loadTimeout)
        if (this.positionSummaryDetail.somePendingRecreatePosition) {
          this.loadTimeout = setTimeout(() => {
            this.loadAllPortfolioByBroker()
          }, 5000)
        }
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.loadingSummary = false
    },
    loadAll() {
      if (this.$refs.portfolioTable) this.$refs.portfolioTable.loadItems()
      this.updateFiltersAndLoadSummaries()
    },
    loadAllPortfolioByBroker() {
      this.$emit('loadAllPortfolioByBroker')
    },
    async createPosition() {
      this.editing = false
      this.isPositionModalActive = true
      gtag.pageview({
        pagePath: '/portfolio/create',
        pageTitle: 'portfolio|create',
        pageContentGroup: 'modal',
      })
    },
    editPosition(position) {
      this.editing = true
      this.isPositionModalActive = true
      this.editItem = JSON.parse(JSON.stringify(position))
      gtag.pageview({
        pagePath: '/portfolio/edit',
        pageTitle: 'portfolio|edit',
        pageContentGroup: 'modal',
      })
    },
    openPositionDetail(position) {
      if (position.pendingRecreate) return

      this.positionTableDrawerActive = true
      this.positionDetail = position

      gtag.pageview({
        pagePath: '/portfolio/detail',
        pageTitle: 'portfolio|detail',
        pageContentGroup: 'modal',
      })
    },
    async downloadPortfolioCsv() {
      this.loadingExport = true

      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const type =
          this.positionType === 'all'
            ? undefined
            : this.positionType === 'allNeCash'
            ? { $ne: 'cash' }
            : this.positionType
        const result = await downloadPositionsCsv({
          brokerId: !this.brokerAll ? this.broker._id : undefined,
          filter: {
            type,
            positionNumber: this.showZeroPositions ? undefined : { $ne: 0 },
            ...this.filters,
          },
        })
        const brokerName = !this.brokerAll ? ` - ${this.broker.alias}` : ''
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(
          blob,
          `Filios - ${this.$t('document.title.portfolio')}${brokerName} ${nowFormatted}.csv`
        )
        gtag.pageview({
          pagePath: `/portfolio/export/success`,
          pageTitle: `portfolio|export|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
        gtag.pageview({
          pagePath: `/portfolio/export/error`,
          pageTitle: `portfolio|export|error`,
          pageContentGroup: 'modal',
        })
      }
      this.loadingExport = false
    },
    filterByTag(tag) {
      if (this.$refs.positionsFilters) this.$refs.positionsFilters.filterByTag(tag)
    },
    customHeaders() {
      if (this.$refs.portfolioTable) this.$refs.portfolioTable.customHeaders()
    },
  },
}
</script>
